import { Fragment, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { ApiError, patch, post, type AdminOutput, type FillMode, type FillQuality, type GraceSource, type OutputFillResult } from '../api'
import { Button, Notice, Panel, Tally, kbps, nodeText, outputState } from '../components/ui'
import { Icon } from '../ui'
import { FILL_MODES, FILL_MODE_SHORT, FILL_QUALITIES, FILL_QUALITY_LABEL, effectiveText, fillShare } from '../fill'
import { ConfirmChange, FromTo } from './Confirm'
import { GraceSettings } from '../live-ui/GraceSettings'
import { graceText } from '../live-ui/grace'
import { getAsOf, nodePath } from './network/api'
import { useCenterNow } from './network/clock'

/**
 * The encoder is away and the output holds its platform session (source grace, D21):
 * a countdown to source_grace_until on the center's clock (X-Server-Time), every second.
 */
function GraceHold({ o, now }: { o: AdminOutput; now: number }) {
  if (!o.source_grace_until) return null
  const left = Math.ceil((Date.parse(o.source_grace_until) - now) / 1000)
  if (!(left > 0)) return null
  return (
    <p className="num mt-0.5 font-mono text-xs text-warn" data-testid={`output-grace-${o.output_id}`} data-left={left} title="OBS is away: the platform session is held until the source grace ends, then the output stops unless OBS is back">
      ↻ holding · {left} s left
    </p>
  )
}

const GRACE_FROM: Record<GraceSource, string> = { own: 'own value', key: "the stream key's", default: "the center's default" }

/** The hold in force (API-UI): "hold 30 s · own value"; nothing on a center that does not send it. */
function GraceSet({ o }: { o: AdminOutput }) {
  if (o.source_grace_ms === undefined || o.source_grace_until) return null
  return (
    <p className="num mt-0.5 text-xs text-muted" data-testid={`output-grace-set-${o.output_id}`} data-ms={o.source_grace_ms} data-source={o.source_grace_source ?? ''}>
      hold {graceText(o.source_grace_ms)}
      {o.source_grace_source ? ` · ${GRACE_FROM[o.source_grace_source]}` : ''}
    </p>
  )
}

export function AdminOutputs() {
  const qc = useQueryClient()
  // getAsOf: the answer's X-Server-Time sets the center's clock for the hold countdown
  const q = useQuery({ queryKey: ['admin-outputs'], queryFn: async () => (await getAsOf<AdminOutput[]>('/api/admin/outputs')).data, refetchInterval: 2000 })
  const now = useCenterNow(1000)
  const [err, setErr] = useState('')
  const [fillFor, setFillFor] = useState<string | null>(null)
  const [graceFor, setGraceFor] = useState<string | null>(null)
  const stop = async (o: AdminOutput) => {
    setErr('')
    try {
      await post(`/api/admin/outputs/${o.output_id}/stop`)
      await qc.invalidateQueries({ queryKey: ['admin-outputs'] })
    } catch (e) {
      setErr((e as Error).message)
    }
  }
  return (
    <Panel title="Outputs" aside={<span className="text-sm text-muted">{q.data?.length ?? 0} on the nodes</span>}>
      <Notice>{err}</Notice>
      <div className="-m-4 overflow-x-auto">
        <table className="w-full min-w-[1040px] text-left text-sm">
          <thead className="border-b border-line text-xs text-muted">
            <tr>
              <th scope="col" className="px-4 py-2 font-medium">Destination</th>
              <th scope="col" className="px-4 py-2 font-medium">User</th>
              <th scope="col" className="px-4 py-2 font-medium">Node</th>
              <th scope="col" className="px-4 py-2 font-medium">State</th>
              <th scope="col" className="px-4 py-2 text-right font-medium" title="What goes to the platform: useful + added filler">Bitrate</th>
              <th scope="col" className="px-4 py-2 font-medium" title="Fill mode in force on the node">Fill</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">Behind</th>
              <th scope="col" className="px-4 py-2 text-right font-medium">Reconnects</th>
              <th scope="col" className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {q.data?.map((o) => {
              const st = outputState(o.state)
              const live = o.state === 'live'
              const added = o.fill_kbps ?? 0
              const share = fillShare(added, o.bitrate_kbps)
              return (
                <Fragment key={o.output_id}>
                  <tr data-testid={`output-${o.output_id}`} data-state={o.state}>
                    <td className="px-4 py-2">
                      <p className="font-medium">{o.name || o.output_id}</p>
                      <p className="max-w-64 truncate text-xs text-muted">{o.destination}</p>
                    </td>
                    <td className="px-4 py-2 text-muted">{o.email || (o.managed ? '—' : 'manual')}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted" data-testid={`output-node-${o.output_id}`}>
                      {o.node ? (
                        <Link to={nodePath(o.node.id)} className="text-text-soft underline-offset-2 hover:text-text hover:underline focus-visible:text-text" title={`Open node ${o.node.name}`} data-testid={`output-node-link-${o.output_id}`}>
                          {nodeText(o.node)}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <span className="flex items-center gap-2">
                        <Tally state={st.lamp} label={st.text} /> {st.text}
                      </span>
                      <GraceHold o={o} now={now} />
                      <GraceSet o={o} />
                      {o.last_error && o.state !== 'live' && o.state !== 'unknown' && <p className="max-w-72 text-xs text-fail">{o.last_error}</p>}
                      {/* E1 (read-only): the output sends another track than the selected one */}
                      {(o.state_reason === 'track_missing' || o.state_reason === 'track_codec_unsupported') && (
                        <p className="max-w-72 text-xs text-warn" data-testid={`output-track-${o.output_id}`}>
                          <Icon name={o.state === 'live' ? 'state-warn' : 'state-fail'} size={12} className="mr-1 inline align-[-1px]" />
                          {o.state_reason === 'track_missing' ? 'The selected track isn’t arriving: sending track 0.' : o.state === 'live' ? 'The selected track’s codec can’t go to this platform: sending track 0.' : 'The selected track’s codec can’t go to this platform.'}
                        </p>
                      )}
                    </td>
                    <td className="num px-4 py-2 text-right">
                      {live ? kbps(o.bitrate_kbps) : '—'}
                      {live && (
                        <span className="block text-xs text-muted" data-testid={`output-bytes-${o.output_id}`} data-useful={Math.round(o.bitrate_kbps - added)} data-added={Math.round(added)}>
                          useful {kbps(o.bitrate_kbps - added)} · added {kbps(added)}
                          {share ? ` (${share}%)` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs" data-testid={`output-fill-${o.output_id}`} data-mode={o.fill_mode ?? ''} data-suspended={!!o.fill_suspended}>
                      {o.fill_mode ? <span className="text-text-soft">{FILL_MODE_SHORT[o.fill_mode]}</span> : <span className="text-muted" title="The node reports no fill mode (older node, or not running)">—</span>}
                      {o.fill_suspended && (
                        <span className="block text-muted" data-testid={`output-fill-paused-${o.output_id}`} title="The node stopped adding filler: the platform reads slowly. Back after 3 s of normal.">
                          paused, slow platform
                        </span>
                      )}
                    </td>
                    <td className="num px-4 py-2 text-right">{live ? `${Math.round(o.lag_ms)} ms` : '—'}</td>
                    <td className="num px-4 py-2 text-right">{o.reconnects}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {o.managed && o.source_grace_ms !== undefined && (
                        <Button variant="quiet" data-testid={`output-grace-edit-${o.output_id}`} aria-expanded={graceFor === o.output_id} onClick={() => (setGraceFor((x) => (x === o.output_id ? null : o.output_id)), setFillFor(null))}>
                          Hold…
                        </Button>
                      )}
                      {o.managed && (
                        <Button variant="quiet" data-testid={`output-fill-edit-${o.output_id}`} aria-expanded={fillFor === o.output_id} onClick={() => (setFillFor((x) => (x === o.output_id ? null : o.output_id)), setGraceFor(null))}>
                          Bitrate…
                        </Button>
                      )}
                      <Button variant="quiet" onClick={() => stop(o)}>
                        Stop
                      </Button>
                    </td>
                  </tr>
                  {graceFor === o.output_id && (
                    <tr>
                      <td colSpan={9} className="bg-panel px-4 py-3">
                        <GraceOverride o={o} onDone={() => void qc.invalidateQueries({ queryKey: ['admin-outputs'] })} onClose={() => setGraceFor(null)} />
                      </td>
                    </tr>
                  )}
                  {fillFor === o.output_id && (
                    <tr>
                      <td colSpan={9} className="bg-panel px-4 py-3">
                        <FillOverride o={o} onDone={() => void qc.invalidateQueries({ queryKey: ['admin-outputs'] })} onClose={() => setFillFor(null)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

const selectCls = 'min-h-9 rounded-md border border-line-2 bg-bg px-2 text-sm text-text focus:border-accent focus:shadow-focus focus:outline-none'

/**
 * Override of one destination's fill (PATCH /api/admin/outputs/{id}/fill): only the fields the
 * admin touched are sent; '' = back to the profile's default (null). Applies from the next frame.
 */
function FillOverride({ o, onDone, onClose }: { o: AdminOutput; onDone: () => void; onClose: () => void }) {
  const known = o.fill_choice
  const [mode, setMode] = useState<string>(known?.mode ?? '')
  const [quality, setQuality] = useState<string>(known?.quality ?? '')
  const [touched, setTouched] = useState<{ mode?: boolean; quality?: boolean }>({})
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [result, setResult] = useState<OutputFillResult | null>(null)
  const modeText = (m: string) => (m ? FILL_MODE_SHORT[m as FillMode] : "Profile's default")
  const qualityText = (x: string) => (x ? FILL_QUALITY_LABEL[x as FillQuality] : 'Default')
  const was = (v: string | null | undefined, text: (s: string) => string) => (known ? text(v ?? '') : 'not reported')
  const dirty = (touched.mode && (!known || mode !== (known.mode ?? ''))) || (touched.quality && (!known || quality !== (known.quality ?? '')))
  const apply = async () => {
    setBusy(true)
    setErr('')
    const body: Record<string, string | null> = {}
    if (touched.mode) body.fill_mode = mode || null
    if (touched.quality) body.fill_quality = quality || null
    try {
      const r = await patch<OutputFillResult>(`/api/admin/outputs/${encodeURIComponent(o.output_id)}/fill`, body)
      setResult(r)
      setConfirm(false)
      setTouched({})
      onDone()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex max-w-3xl flex-col gap-3" data-testid="output-fill-form">
      <p className="text-sm">
        <span className="font-semibold">Bitrate for {o.name || o.output_id}</span>
        <span className="text-muted"> · {o.email}</span>
      </p>
      <p className="text-xs text-muted" data-testid="output-fill-now">
        On the node: {o.fill_mode ? FILL_MODE_SHORT[o.fill_mode] : 'not reported'}
        {o.fill_effective ? ` · resolved by the center: ${effectiveText(o.fill_effective)}` : ''}
        {!known && ' · the list does not report the destination’s own choice, so only the fields you change are sent'}
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">Mode</span>
          <select aria-label="Mode" data-testid="output-fill-mode" className={selectCls} value={mode} disabled={busy || confirm} onChange={(e) => { setMode(e.target.value); setTouched((t) => ({ ...t, mode: true })) }}>
            <option value="">Profile's default</option>
            {FILL_MODES.map((m) => (
              <option key={m} value={m}>
                {FILL_MODE_SHORT[m]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">Quality (Fill to floor)</span>
          <select aria-label="Quality" data-testid="output-fill-quality" className={selectCls} value={quality} disabled={busy || confirm} onChange={(e) => { setQuality(e.target.value); setTouched((t) => ({ ...t, quality: true })) }}>
            <option value="">Default</option>
            {FILL_QUALITIES.map((x) => (
              <option key={x} value={x}>
                {FILL_QUALITY_LABEL[x]}
              </option>
            ))}
          </select>
        </label>
        {!confirm && (
          <>
            <button
              type="button"
              data-testid="output-fill-save"
              disabled={!dirty || busy}
              onClick={() => { setConfirm(true); setResult(null) }}
              className="min-h-9 rounded-md border border-live bg-live/[.12] px-4 text-sm font-semibold text-live disabled:border-line-2 disabled:bg-transparent disabled:text-faint"
            >
              Save…
            </button>
            <Button variant="quiet" onClick={onClose}>
              Close
            </Button>
          </>
        )}
      </div>
      {confirm && (
        <ConfirmChange testId="output-fill-confirm" title={`Override the bitrate of ${o.name || o.output_id}?`} busy={busy} onApply={() => void apply()} onCancel={() => setConfirm(false)}>
          {touched.mode && <FromTo label="Mode" from={was(known?.mode, modeText)} to={modeText(mode)} />}
          {touched.quality && <FromTo label="Quality" from={was(known?.quality, qualityText)} to={qualityText(quality)} />}
          <p>Applies from the next frame, without reconnecting. The user sees the change if bitrate fill is on for them. Recorded in the log.</p>
        </ConfirmChange>
      )}
      {result && (
        <p role="status" data-testid="output-fill-result" className="text-sm text-live">
          ✓ Saved. In force: {effectiveText(result.fill_effective)}, from the next frame.
        </p>
      )}
      {err && (
        <p role="alert" data-testid="output-fill-error" className="text-sm text-fail">
          Not saved: {err}
        </p>
      )}
    </div>
  )
}

/**
 * The admin's change of one destination's hold when OBS drops (API-UI: PATCH
 * /api/admin/outputs/{id}/grace {source_grace_ms: ms | null}), like the bitrate
 * override: the destination's own value, or null = as the stream key (else the
 * center's default). Applies at once, no reconnect, no confirmation by the center
 * (the air does not break); recorded in the log with the previous value. Shown only
 * when the list reports source_grace_ms (a center with API-UI).
 */
function GraceOverride({ o, onDone, onClose }: { o: AdminOutput; onDone: () => void; onClose: () => void }) {
  const saved = o.source_grace_source === 'own' ? (o.source_grace_ms ?? null) : null
  const inherited = o.source_grace_source && o.source_grace_source !== 'own' ? o.source_grace_ms : undefined
  const [value, setValue] = useState<number | null>(saved)
  const [invalid, setInvalid] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [result, setResult] = useState('')
  const name = o.name || o.output_id
  const text = (v: number | null) => (v === null ? `as the stream key${inherited !== undefined ? ` (${graceText(inherited)} now)` : ''}` : graceText(v))
  const dirty = value !== saved && !invalid
  const apply = async () => {
    setBusy(true)
    setErr('')
    try {
      const r = await patch<{ source_grace_effective_ms?: number; source_grace_source?: GraceSource } | undefined>(`/api/admin/outputs/${encodeURIComponent(o.output_id)}/grace`, { source_grace_ms: value })
      const force = r?.source_grace_effective_ms !== undefined ? ` · in force ${graceText(r.source_grace_effective_ms)}${r.source_grace_source ? `, ${GRACE_FROM[r.source_grace_source]}` : ''}` : ''
      setResult(`✓ Saved: ${text(value)}${force}. Applies at once, no reconnect.`)
      setConfirm(false)
      onDone()
    } catch (e) {
      // no such handler on an older center: the mux's own 405 / "404 page not found" (not a JSON error)
      if (e instanceof ApiError && (e.status === 405 || (e.status === 404 && e.data === undefined))) setErr('this center has no admin change of the hold yet (PATCH …/grace). The user changes it in the destination’s Advanced.')
      else if (e instanceof ApiError && e.status === 404) setErr('this output is gone. Reload the list.')
      else if (e instanceof ApiError && e.status === 409) setErr('the stream key of this destination is revoked.')
      else if (e instanceof ApiError && e.status === 400) setErr(`the server refused this value (${e.message}).`)
      else setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex max-w-xl flex-col gap-3" data-testid="output-grace-form">
      <p className="text-sm">
        <span className="font-semibold">Hold for {name}</span>
        <span className="text-muted"> · {o.email}</span>
      </p>
      <GraceSettings
        value={value}
        saved={saved}
        inherited={inherited}
        inheritLabel="As the stream key"
        inheritFrom="the stream key's or the center's default"
        subject={name}
        disabled={busy || confirm}
        onChange={(v, bad) => (setValue(v), setInvalid(bad), setResult(''))}
        testId="output-grace"
      />
      {!confirm && (
        <div className="flex gap-2">
          <Button variant="primary" data-testid="output-grace-save" disabled={!dirty || busy} onClick={() => (setConfirm(true), setResult(''))}>
            Save…
          </Button>
          <Button variant="quiet" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
      {confirm && (
        <ConfirmChange testId="output-grace-confirm" title={`Change the hold of ${name}?`} busy={busy} onApply={() => void apply()} onCancel={() => setConfirm(false)}>
          <FromTo label="Hold when OBS drops" from={text(saved)} to={text(value)} />
          <p>Applies at once, without reconnecting the platform. The user sees it in the destination’s Advanced. Recorded in the log with the previous value.</p>
        </ConfirmChange>
      )}
      {result && (
        <p role="status" data-testid="output-grace-result" className="text-sm text-live">
          {result}
        </p>
      )}
      {err && (
        <p role="alert" data-testid="output-grace-error" className="text-sm text-fail">
          Not saved: {err}
        </p>
      )}
    </div>
  )
}
